<script lang="ts">
import { defineComponent, useAttrs, computed } from 'vue'
import { defaultProps, defaultFunction } from '@/components/_parials/buttonCore'

export default defineComponent({
  name: 'ButtonSubmit',

  /**
   * @description ルート要素への属性の自動引き継ぎを止め、rootAttrs で明示的に渡す
   */
  inheritAttrs: false,

  props: {
    ...defaultProps,

    caption: {
      type: String,
      default: '',
    },

    /**
    * Form Submit ボタンかどうか
    * @description isLink が true の場合は本設定は無視される
    * @description リンクではなく、且つ submit も false だった場合は、ただの button 要素になる
    */
    isSubmit: {
      type: Boolean,
      default: false,
    },

    /**
     * ボタンの色を独自のカラーにする場合に指定する
     * @description 「#」から始まる 16 進数のカラーコードを指定する
     */
    color: {
      type: String,
      default: '',
      validator(value: string) {
        // 空文字なら OK（デフォルト扱い）
        if (value === '') return true

        // #RRGGBB の形式か判定
        return /^#([0-9a-fA-F]{6})$/.test(value)
      },
    },
  },

  setup(props, { emit }) {
    const {
      // data
      wrapper,

      // computed
      buttonTagName,

      // methods
      runEvent,
    } = defaultFunction(props, emit)

    const attrs = useAttrs()

    const buttonType = computed(() => {
      return props.isSubmit ? 'submit' : 'button'
    })

    /**
     * ルート要素へ引き渡す属性
     *
     * @description
     *  親が付けた emitName のリスナー（既定では @click）だけを除外する。
     *  ルート要素はボタン本体より広いレイアウト用の箱であり（width props でボタン幅を絞ると左右に余白ができる）、
     *  Vue の属性フォールスルーでここにリスナーが乗ると、ボタン外側の余白をクリックしただけで
     *  runEvent（prevent / stop の適用箇所）を経ずに親のハンドラが走ってしまう。
     *  リスナーの発火は runEvent の emit が担うため、ルート要素からは外す。
     */
    const rootAttrs = computed(() => {
      const handlerKey = `on${props.emitName.charAt(0).toUpperCase()}${props.emitName.slice(1)}`

      /**
       * 対象のリスナーのキーかどうか
       * @description 修飾子付きの記述（@click.once → onClickOnce）は別キーになるため、接尾辞も許容する
       */
      const isHandlerKey = (key: string): boolean => {
        if (key === handlerKey) return true
        return key.startsWith(handlerKey) && /^(Once|Capture|Passive)+$/.test(key.slice(handlerKey.length))
      }

      return Object.fromEntries(Object.entries(attrs).filter(([key]) => !isHandlerKey(key)))
    })

    return {
      // data
      wrapper,

      // computed
      buttonTagName,
      buttonType,
      rootAttrs,

      // methods
      runEvent,
    }
  }
})
</script>

<template>
  <div
    ref="wrapper"
    v-bind="rootAttrs"
    :class="[
      'o-action__submit',
      { 'is-small': isFontSmall },
      { 'is-bold': isFontBold },
      { 'is-shadow': hasShadow },
      { 'is-autoWidth': autoWidth },
      { 'is-large': isLarge },
      { 'is-narrowMargin': narrowMargin },
    ]"
    :style="{
      '--max-width': width ? `${width}px` : 'none',
      ...(color && { '--button-color': color }),
    }"
    :data-color-override="!!color">
    <p
      v-if="caption"
      class="o-action__submit--caption"
      v-html="caption" />

    <a
      v-if="isLink"
      class="o-action__submit--item"
      :href="href"
      :target="target">
      <span class="o-action__submit--label">
        <slot />
      </span>
    </a>
    <button
      v-else
      class="o-action__submit--item"
      :type="buttonType"
      :name="name"
      :disabled="disabled"
      @click="runEvent">
      <span class="o-action__submit--label">
        <slot />
      </span>
    </button>
  </div>
</template>

<style lang="sass" scoped>
.o-action__submit
  $root: &
  $margin-size-px: 24
  $margin-narrow-size-px: 16
  --max-width: none
  --button-color: ''
  margin:
    top: #{math.div($margin-size-px, $font-normal-px)}em
    left: auto
    right: auto

  &:first-child
    margin-top: 0

  & &--item
    $button-height-px: 48
    width: 100%
    max-width: var(--max-width)
    min-height: changeSizePx2Em($button-height-px)
    display: flex
    justify-content: center
    align-items: center
    position: relative
    line-height: 1.4
    color: $font-white
    font-size: $font-normal
    text:
      align: center
      decoration: none
    margin:
      left: auto
      right: auto
    padding: 0.5em 1.2em
    +transition($duration: $transition-middle)

    &:disabled
      color: $font-xx-light-gray

  & &--label
    display: flex
    justify-content: center
    align-items: center
    position: relative
    z-index: 1

  & &--caption
    font-size: $font-small
    text-align: center
    margin-bottom: 0.5em

  &[data-color-override="false"]
    #{$root}--item
      &::before,
      &::after
        content: ""
        width: 100%
        height: 100%
        display: block
        position: absolute
        top: 0
        left: 0
        border-radius: 100vh
        box-sizing: border-box
        +transition

      &::before
        background-image: linear-gradient(to bottom, #ffae78 0%,#f08f4f 100%)
        border: 1px solid $border-light-orange

      &::after
        background-image: linear-gradient(to bottom, #efa370 0%, $bg-dark-orange 100%)
        border: 1px solid $border-dark-orange

      &:not(:disabled)
        +mouseover
          &::after
            opacity: 0

      &:disabled
        &::after
          background-color: rgba($bg-light-gray, 1)
          background-image: none
          border: 1px solid $bg-light-gray

  &[data-color-override="true"]
    #{$root}--item
      overflow: hidden
      border-radius: 100vh
      background-color: var(--button-color)

      &::before
        content: ""
        width: 100%
        height: 100%
        display: block
        position: absolute
        top: 0
        left: 0
        background-color: rgba($bg-white, 1)
        opacity: 0
        visibility: hidden
        +transition

      &:disabled
        background-color: rgba($bg-light-gray, 1)

      &:not(:disabled)
        +mouseover
          &::before
            opacity: 0.4
            visibility: visible

  &.is-small
    $button-height-px: 40
    #{$root}--item
      min-height: changeSizePx2Em($button-height-px, $font-small-px)
      font-size: $font-small
      padding: 0.5em 1.8em

  &.is-bold
    #{$root}--item
      font-weight: bold

  &.is-shadow
    #{$root}--item
      &:not(:disabled)
        +mouseover
          &::before,
          &::after
            box-shadow: $button-shadow

  &.is-autoWidth
    display: flex
    justify-content: center

    #{$root}--item
      width: auto

  &.is-narrowMargin
    margin-top: #{math.div($margin-narrow-size-px, $font-normal-px)}em

    &:first-child
      margin-top: 0

  &.is-large
    $button-height-px: 54
    #{$root}--item
      min-height: changeSizePx2Em($button-height-px)
      padding:
        top: 0.6em
        bottom: 0.6em

  +breakpoint
    $margin-size-px: 16
    margin-top: #{math.div($margin-size-px, $font-normal-px)}em

    &:first-child
      margin-top: 0
</style>
